import React from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Boxes, ShieldCheck, BarChart3, BookOpen, ChevronRight, ShoppingBag } from 'lucide-react';

const TALLY_ENABLED = process.env.REACT_APP_FEATURE_TALLY !== 'false';

/**
 * Admin side drawer — hosts less-frequent admin sections that don't fit in the
 * primary bottom nav (Inventory, Admin team, Stats, and optionally Tally).
 */
export default function AdminSideNav({ open, onOpenChange, active, onSelect }) {
  const items = [
    { value: 'requirements', label: 'Buyer Requirements', description: 'Moderate open buyer posts and callbacks.', icon: ShoppingBag, testId: 'admin-side-requirements' },
    { value: 'inventory', label: 'Inventory', description: 'Manage stock, purchases and sales.', icon: Boxes, testId: 'admin-side-inventory' },
    { value: 'team', label: 'Admins', description: 'Add or revoke admin access.', icon: ShieldCheck, testId: 'admin-side-team' },
    { value: 'analytics', label: 'Stats', description: 'Platform analytics and reports.', icon: BarChart3, testId: 'admin-side-analytics' },
    ...(TALLY_ENABLED ? [{ value: 'tally', label: 'Tally Sync', description: 'Accounting exports & live sync.', icon: BookOpen, testId: 'admin-side-tally' }] : []),
  ];

  const handleClick = (value) => {
    onSelect(value);
    onOpenChange(false);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="left"
        data-testid="admin-side-nav"
        className="p-0 w-72 max-w-[85vw] bg-[#F6F8FB] flex flex-col"
      >
        <SheetHeader className="px-5 pt-6 pb-4 text-left">
          <SheetTitle className="text-lg font-bold text-[#0B1620] font-['Bricolage_Grotesque'] tracking-tight">
            More admin
          </SheetTitle>
          <SheetDescription className="text-[13px] text-[#5B6675]">
            Deeper controls for inventory, admins, analytics, and integrations.
          </SheetDescription>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-3 pb-6 space-y-1">
          {items.map((it) => {
            const Icon = it.icon;
            const isActive = active === it.value;
            return (
              <button
                key={it.value}
                data-testid={it.testId}
                onClick={() => handleClick(it.value)}
                className={`w-full flex items-center gap-3 px-3 py-3 rounded-2xl transition-colors text-left ${
                  isActive
                    ? 'bg-[#0B4F3F]/8 border border-[#0B4F3F]/20'
                    : 'hover:bg-[#EFF2F7] border border-transparent'
                }`}
              >
                <div
                  className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
                    isActive ? 'bg-[#0B4F3F] text-white' : 'bg-[#EFF2F7] text-[#0B4F3F]'
                  }`}
                >
                  <Icon className="w-4.5 h-4.5" strokeWidth={2} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className={`text-[14px] font-semibold ${isActive ? 'text-[#0B4F3F]' : 'text-[#0B1620]'}`}>
                    {it.label}
                  </p>
                  <p className="text-[11.5px] text-[#5B6675] truncate">{it.description}</p>
                </div>
                <ChevronRight className="w-4 h-4 text-[#5B6675] flex-shrink-0" />
              </button>
            );
          })}
        </div>
      </SheetContent>
    </Sheet>
  );
}
